/**
 * Challenge Validation Schemas
 *
 * Zod schemas for validating challenge forms and API requests.
 * Provides client-side validation that matches server-side validation rules.
 *
 * Features:
 * - Challenge creation validation
 * - Challenge participants management
 * - Participant status updates (accept/decline/rescind)
 * - Date range validation (end date after start date)
 */

import { templateDescriptionSchema } from './common.schemas';
import { todayOrFutureDateSchema, singleDateSchema } from './date.schemas';
import { z } from './zod';
import { dayjs } from '../utils/date';
import {
  getChallengeTypeOptions,
  createEnumFromOptions,
} from '../utils/select-options';

// =============================================================================
// COMMON VALIDATION RULES
// =============================================================================

/**
 * Type validation
 */
const typeSchema = createEnumFromOptions(getChallengeTypeOptions());

// =============================================================================
// CHALLENGE VALIDATION SCHEMAS
// =============================================================================

/**
 * Base challenge object schema (without refinements)
 */
const baseChallengeSchema = z.object({
  id: z.string().optional(),
  title: z
    .string()
    .min(1, 'Title is required')
    .max(255, 'Title must be less than 255 characters')
    .trim(),
  description: templateDescriptionSchema,
  type: typeSchema,
  challengeable: z.string().min(1, 'Challenge scope is required'),
  participants: z.array(z.string().min(1, 'Participant is required')),
  metadata: z.array(z.string()).optional(),
  start_date: todayOrFutureDateSchema,
  end_date: singleDateSchema,
});

/**
 * Challenge form validation schema
 */
export const challengeSchema = baseChallengeSchema.refine(
  (data) => dayjs(data.end_date).isAfter(dayjs(data.start_date), 'day'),
  {
    message: 'End date must be after start date',
    path: ['end_date'],
  }
);

/**
 * Update challenge schema (partial for PATCH requests)
 * Note: Uses base schema without date refinement since partial updates may not include both dates
 */
export const updateChallengeSchema = baseChallengeSchema.partial();

// =============================================================================
// CHALLENGE PARTICIPANTS VALIDATION SCHEMAS
// =============================================================================

/**
 * Challenge participants form validation schema
 */
export const challengeParticipantsSchema = z.object({
  participants: z.array(z.string().min(1, 'Participant is required')),
});

/**
 * Update challenge participants schema (partial for PATCH requests)
 */
export const updateChallengeParticipantsSchema =
  challengeParticipantsSchema.partial();

// =============================================================================
// CHALLENGE PARTICIPANT STATUS VALIDATION SCHEMAS
// =============================================================================

/**
 * Challenge participant status form validation schema
 */
export const challengeParticipantStatusSchema = z.object({
  action: z.enum(['accept', 'decline', 'rescind']),
});

/**
 * Update challenge participant status schema (partial for PATCH requests)
 */
export const updateChallengeParticipantStatusSchema =
  challengeParticipantStatusSchema.partial();

// =============================================================================
// TYPE EXPORTS
// =============================================================================

export type ChallengeInput = z.input<typeof challengeSchema>;
export type ChallengeOutput = z.output<typeof challengeSchema>;
export type UpdateChallengeInput = z.input<typeof updateChallengeSchema>;
export type UpdateChallengeOutput = z.output<typeof updateChallengeSchema>;

export type ChallengeParticipantsInput = z.input<
  typeof challengeParticipantsSchema
>;
export type ChallengeParticipantsOutput = z.output<
  typeof challengeParticipantsSchema
>;
export type UpdateChallengeParticipantsInput = z.input<
  typeof updateChallengeParticipantsSchema
>;
export type UpdateChallengeParticipantsOutput = z.output<
  typeof updateChallengeParticipantsSchema
>;

export type ChallengeParticipantStatusInput = z.input<
  typeof challengeParticipantStatusSchema
>;
export type ChallengeParticipantStatusOutput = z.output<
  typeof challengeParticipantStatusSchema
>;
export type UpdateChallengeParticipantStatusInput = z.input<
  typeof updateChallengeParticipantStatusSchema
>;
export type UpdateChallengeParticipantStatusOutput = z.output<
  typeof updateChallengeParticipantStatusSchema
>;

// =============================================================================
// VALIDATION HELPER FUNCTIONS
// =============================================================================

/**
 * Validate challenge data
 * @param data - The data to validate
 * @returns The validated and typed challenge data
 * @throws ZodError if validation fails
 */
export function validateChallenge(data: unknown): ChallengeOutput {
  return challengeSchema.parse(data);
}

/**
 * Safely validate challenge data
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateChallenge(data: unknown) {
  return challengeSchema.safeParse(data);
}

/**
 * Check if data is valid challenge data
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidChallenge(data: unknown): boolean {
  return challengeSchema.safeParse(data).success;
}

/**
 * Validate update challenge data
 * @param data - The data to validate
 * @returns The validated and typed update challenge data
 * @throws ZodError if validation fails
 */
export function validateUpdateChallenge(data: unknown): UpdateChallengeOutput {
  return updateChallengeSchema.parse(data);
}

/**
 * Safely validate update challenge data
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateUpdateChallenge(data: unknown) {
  return updateChallengeSchema.safeParse(data);
}

/**
 * Check if data is valid update challenge data
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidUpdateChallenge(data: unknown): boolean {
  return updateChallengeSchema.safeParse(data).success;
}

/**
 * Validate challenge participants data
 * @param data - The data to validate
 * @returns The validated and typed challenge participants data
 * @throws ZodError if validation fails
 */
export function validateChallengeParticipants(
  data: unknown
): ChallengeParticipantsOutput {
  return challengeParticipantsSchema.parse(data);
}

/**
 * Safely validate challenge participants data
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateChallengeParticipants(data: unknown) {
  return challengeParticipantsSchema.safeParse(data);
}

/**
 * Check if data is valid challenge participants data
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidChallengeParticipants(data: unknown): boolean {
  return challengeParticipantsSchema.safeParse(data).success;
}

/**
 * Validate update challenge participants data
 * @param data - The data to validate
 * @returns The validated and typed update challenge participants data
 * @throws ZodError if validation fails
 */
export function validateUpdateChallengeParticipants(
  data: unknown
): UpdateChallengeParticipantsOutput {
  return updateChallengeParticipantsSchema.parse(data);
}

/**
 * Safely validate update challenge participants data
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateUpdateChallengeParticipants(data: unknown) {
  return updateChallengeParticipantsSchema.safeParse(data);
}

/**
 * Check if data is valid update challenge participants data
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidUpdateChallengeParticipants(data: unknown): boolean {
  return updateChallengeParticipantsSchema.safeParse(data).success;
}

/**
 * Validate challenge participant status data
 * @param data - The data to validate
 * @returns The validated and typed challenge participant status data
 * @throws ZodError if validation fails
 */
export function validateChallengeParticipantStatus(
  data: unknown
): ChallengeParticipantStatusOutput {
  return challengeParticipantStatusSchema.parse(data);
}

/**
 * Safely validate challenge participant status data
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateChallengeParticipantStatus(data: unknown) {
  return challengeParticipantStatusSchema.safeParse(data);
}

/**
 * Check if data is valid challenge participant status data
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidChallengeParticipantStatus(data: unknown): boolean {
  return challengeParticipantStatusSchema.safeParse(data).success;
}

/**
 * Validate update challenge participant status data
 * @param data - The data to validate
 * @returns The validated and typed update challenge participant status data
 * @throws ZodError if validation fails
 */
export function validateUpdateChallengeParticipantStatus(
  data: unknown
): UpdateChallengeParticipantStatusOutput {
  return updateChallengeParticipantStatusSchema.parse(data);
}

/**
 * Safely validate update challenge participant status data
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateUpdateChallengeParticipantStatus(data: unknown) {
  return updateChallengeParticipantStatusSchema.safeParse(data);
}

/**
 * Check if data is valid update challenge participant status data
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidUpdateChallengeParticipantStatus(
  data: unknown
): boolean {
  return updateChallengeParticipantStatusSchema.safeParse(data).success;
}
